import { useState, lazy, Suspense } from "react";

import { getErrorMessage } from "utils/error";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "providers/Layout";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import {
  ArrowLeft,
  Calender,
  ChevronDown,
  ChevronUp,
  Database01,
  Plus,
  Settings02
} from "untitledui-js-base";
import moment from "moment";
import addressApi from "api/landlord/address";
const UnitSettings = lazy(() => import("./components/UnitSettings"));
import {
  getPropertyNameWithLongAddress,
  getPropertyNameWithShortAddress,
  getShortAddressFromAddress
} from "shared/addressUtils";
import { useAuthContext } from "providers/Auth";
import { DeleteUnitModal } from "../../shared/app/UnitsTableView/components/DeleteUnitModal";
import { unitStatus } from "shared/app/UnitsTableView/constants";
import { ApplicationsTableView } from "shared/app/ApplicationsTableView";
import { Loader } from "shared/components/Loader";
import { Tabs } from "shared/components/Tabs";
import { Redirect } from "shared/components/Redirect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Unit } from "types/landlord/address";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { toast } from "react-toastify";
import { EditConfirmModal } from "views/properties-id-units-id/components/EditConfirmModal";
import { instance } from "api/apiBase";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Switch } from "shared/components/forms/Switch";
import { useSettingsController } from "views/settings-integration/controller";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { useMedia } from "shared/providers";
import {
  EditModalContext,
  EditUnitSettings
} from "views/properties-id-units-id/types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import {
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types/partner";
import { RentAmount } from "shared/components/RentAmount";

export const UnitDetail = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const queryClient = useQueryClient();
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { type, partner, partnerFeatures } = useAuthContext();
  const { isEverySyncEnabled } = useSettingsController();

  const [showSettings, setShowSettings] = useState(false);
  // MobileView
  const [showDetails, setShowDetails] = useState(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [tab, setActiveTab] = useState<"Applications">("Applications");

  const [showDeleteUnitModal, setShowDeleteUnitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<{
    isOpen: boolean;
    context?: EditModalContext;
  }>({
    isOpen: false,
    context: undefined
  });

  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  const isMA =
    partner?.property_management_system?.name ===
    PropertyManagementSystem.ManageAmerica;

  const {
    data: unit,
    isLoading,
    refetch
  } = useQuery<Unit>({
    queryKey: ["unit", unitId],
    queryFn: () => addressApi.getUnitById(Number(unitId))
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["unit", unitId],
    mutationFn: async ({
      unitId,
      settings
    }: {
      unitId: number;
      settings: EditUnitSettings;
    }) => {
      await instance.patch(`/portal/v1/units/${unitId}`, { ...settings });
    },
    onSuccess: () => {
      setShowEditModal({ isOpen: false });
      refetch();
    }
  });

  const isDisabled = !unit?.magic_link?.active;

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(
        unit?.magic_link?.application_link || ""
      );
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const deleteUnit = () => {
    return toast
      .promise(addressApi.deleteUnit(Number(unit?.id)), {
        pending: `Deleting ${dictionary.unit}...`,
        success: `${dictionary.Unit} deleted`,
        error: `Failed to delete ${dictionary.unit}`
      })
      .then(() => {
        setShowDeleteUnitModal(false);
        setShowSettings(false);
        navigate("/units");
      });
  };

  if (type.isSDK && type.isBoomScreen) return <Redirect href="/" />;

  if (isLoading)
    return (
      <Layout>
        <div className="flex items-center justify-center w-full h-full">
          <Loader />
        </div>
      </Layout>
    );
  return (
    <Layout
      footerClassName="p-4 border-t border-alpha-100"
      footer={
        <Show if={isMobile && type.isBoomScreen}>
          <div className="flex items-center gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={handleCopyClick}
              className="md:ml-6 w-full sm:w-auto"
            >
              Copy application link
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={() => setShowInviteApplicantsModal(true)}
              className="w-full sm:w-auto"
              Icon={Plus}
            >
              Invite applicants
            </Button>
          </div>
        </Show>
      }
      headerClassName="py-0 md:py-3"
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 py-2 md:py-0 pl-3 md:px-6 md:pb-3">
            {isMobile ? (
              <Button
                borderLess
                size={ButtonSize.SM}
                Icon={ArrowLeft}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Back
              </Button>
            ) : (
              <BreadCrumbs
                crumbs={
                  unit?.property
                    ? [
                        {
                          title: dictionary.Properties,
                          href: "/properties"
                        },
                        {
                          title:
                            unit.property.name ||
                            getShortAddressFromAddress(unit.property.address) ||
                            `current ${dictionary.property}`,
                          href: `/properties/${unit.property.id}`
                        },
                        {
                          title: unit.title,
                          href: `/properties/${unit.property.id}/units/${unit.id}`
                        }
                      ]
                    : []
                }
              />
            )}

            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="stroke-content-400 text-content-400 hover:bg-alpha-40"
              onClick={() => setShowSettings(true)}
              Icon={Settings02}
            >
              <p className="hidden md:flex">{dictionary.Unit} Settings</p>
            </Button>
          </div>
          <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-y-2 border-b border-alpha-40 px-6 md:py-4">
            <div className="flex w-full flex-col gap-2 border-b md:border-none border-alpha-40 py-4 md:py-0">
              {unit?.property ? (
                <p className="text-xs md:text-sm text-content-500 order-2 md:order-1 break-all">
                  {isMobile
                    ? getPropertyNameWithShortAddress(
                        unit.property,
                        unit.property.address
                      )
                    : getPropertyNameWithLongAddress(
                        unit.property,
                        unit.property.address
                      )}
                </p>
              ) : null}

              {isMobile ? (
                <div className="flex justify-between items-center">
                  <h4 className="truncate max-w-[220px] text-xl font-bold text-content-1000">
                    {unit?.title}
                  </h4>

                  <Badge
                    size={BadgeSize.SM}
                    variant={
                      unit?.occupied
                        ? BadgeVariant.PRIMARY
                        : BadgeVariant.SUCCESS
                    }
                  >
                    {unitStatus(Boolean(unit?.occupied))}
                  </Badge>
                </div>
              ) : (
                <h4 className="text-xl font-bold text-content-1000 order-1 md:order-2">
                  {unit?.title}
                </h4>
              )}
            </div>

            {type.isBoomScreen ? (
              <div className="w-full md:ml-auto py-4 md:py-0 flex items-center md:justify-end gap-2 flex-wrap md:flex-nowrap">
                {isEverySyncEnabled && unit?.pms_id ? (
                  <AllowOrDisallowApplications
                    isActive={Boolean(unit.magic_link?.active)}
                  />
                ) : (
                  <Switch
                    label="Allow applications"
                    checked={Boolean(unit?.magic_link?.active)}
                    className="text-sm md:text-base font-medium text-content-1000"
                    onChange={async () => {
                      try {
                        await addressApi.updateMagicLink(
                          Number(unit?.magic_link?.id),
                          { active: !unit?.magic_link?.active }
                        );
                        queryClient.invalidateQueries({
                          queryKey: ["unit", unitId]
                        });
                      } catch (err) {
                        toast.error(getErrorMessage(err));
                      }
                    }}
                  />
                )}

                <Show if={!isMobile}>
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <Button
                      variant={ButtonVariant.BASE}
                      size={ButtonSize.SM}
                      onClick={handleCopyClick}
                      className="md:ml-6 w-full sm:w-auto"
                    >
                      Copy application link
                    </Button>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SM}
                      onClick={() => setShowInviteApplicantsModal(true)}
                      className="w-full sm:w-auto"
                      Icon={Plus}
                      disabled={isDisabled}
                      tooltip={
                        isDisabled
                          ? `You can invite applicants to a ${dictionary.unit} that accepts applications`
                          : undefined
                      }
                      tooltipPlacement="bottom-start"
                    >
                      Invite applicants
                    </Button>
                  </div>
                </Show>
              </div>
            ) : null}
          </div>
          {isMobile ? (
            <div className="pl-3 py-2">
              <Show if={!showDetails}>
                <Button
                  borderLess
                  size={ButtonSize.SM}
                  Icon={ChevronDown}
                  onClick={() => setShowDetails(true)}
                >
                  Show details
                </Button>
              </Show>

              <Show if={showDetails}>
                <div className="flex flex-col gap-1.5 px-4 pt-4">
                  <RentAmount
                    value={
                      type.isPMS
                        ? unit?.pms_monthly_rent_amount
                        : unit?.monthly_rent_amount
                    }
                    isMobile
                    title={isMA ? "Total rent" : "Rent amount"}
                  />
                  <Show if={isMA}>
                    <RentAmount
                      value={unit?.home_rent_amount}
                      title="Home rent"
                      isMobile
                    />
                    <RentAmount
                      value={unit?.site_rent_amount}
                      title="Site rent"
                      isMobile
                    />
                  </Show>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database01
                        className="shrink-0 stroke-content-500"
                        size="20"
                      />
                      <p className="font-medium text-sm text-content-500">
                        Source
                      </p>
                    </div>

                    <p className="font-medium text-sm text-content-500">
                      {unit?.pms_id ? "PMS" : "Manually added"}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calender
                        className="shrink-0 stroke-content-500"
                        size="20"
                      />
                      <p className="font-medium text-sm text-content-500">
                        Available
                      </p>
                    </div>

                    <p className="font-medium text-sm text-content-500">
                      {unit?.available_at
                        ? moment(unit.available_at).utc().format("MMM D, YYYY")
                        : "--"}
                    </p>
                  </div>
                </div>
              </Show>

              <Show if={showDetails}>
                <Button
                  borderLess
                  size={ButtonSize.SM}
                  Icon={ChevronUp}
                  className="mt-2"
                  onClick={() => setShowDetails(false)}
                >
                  Hide details
                </Button>
              </Show>
            </div>
          ) : (
            <div className="flex md:items-center justify-start gap-2 md:gap-12 px-3 md:px-6 py-4 pb-2 flex-wrap md:flex-nowrap">
              <div>
                <p className="m-0 text-sm font-normal text-content-400">
                  Current Status
                </p>
                <p className="m-0 text-sm font-medium capitalize text-content-1000">
                  {unitStatus(Boolean(unit?.occupied))}
                  {!unit?.pms_occupied_override &&
                  unit?.pms_occupied !== unit?.occupied ? (
                    <span className="text-content-200">
                      {" "}
                      (Manually overridden)
                    </span>
                  ) : null}
                </p>
              </div>
              <div>
                <p className="m-0 text-sm font-normal text-content-400">
                  {isMA ? "Total rent" : "Monthly Rent"}
                </p>
                <RentAmount
                  value={unit?.monthly_rent_amount}
                  title={isMA ? "Total rent" : "Monthly Rent"}
                  className="m-0 text-sm"
                  isManuallyOverridden={
                    !unit?.pms_monthly_rent_amount_override &&
                    unit?.pms_monthly_rent_amount?.cents !==
                      unit?.monthly_rent_amount?.cents
                  }
                />
              </div>
              <Show if={isMA}>
                <div>
                  <p className="m-0 text-sm font-normal text-content-400">
                    Home rent
                  </p>
                  <RentAmount
                    value={unit?.home_rent_amount}
                    className="m-0 text-sm"
                    isManuallyOverridden={
                      !unit?.pms_home_rent_amount_override &&
                      unit?.pms_home_rent_amount?.cents !==
                        unit?.home_rent_amount?.cents
                    }
                  />
                </div>
                <div>
                  <p className="m-0 text-sm font-normal text-content-400">
                    Site rent
                  </p>
                  <RentAmount
                    value={unit?.site_rent_amount}
                    className="m-0 text-sm"
                    isManuallyOverridden={
                      !unit?.pms_site_rent_amount_override &&
                      unit?.pms_site_rent_amount?.cents !==
                        unit?.site_rent_amount?.cents
                    }
                  />
                </div>
              </Show>
              <div>
                <p className="m-0 text-sm font-normal text-content-400">
                  Available
                </p>
                <p className="m-0 text-sm font-medium text-content-1000">
                  {unit?.available_at
                    ? moment(unit.available_at).utc().format("MMM D, YYYY")
                    : "--"}
                  {!unit?.pms_available_at_override &&
                  unit?.pms_available_at !== unit?.available_at ? (
                    <span className="text-content-200">
                      {" "}
                      (Manually overridden)
                    </span>
                  ) : null}
                </p>
              </div>
              <div>
                <p className="m-0 text-sm font-normal text-content-400">
                  Source
                </p>
                <p className="m-0 text-sm font-medium text-content-1000">
                  {unit?.pms_id ? "PMS" : "Manually added"}
                </p>
              </div>
              <Show if={hasPropertyOwner}>
                <div>
                  <p className="m-0 text-sm font-normal text-content-400">
                    Owner
                  </p>
                  <p className="m-0 text-sm font-medium text-content-1000">
                    {unit?.property.owner || "--"}
                  </p>
                </div>
              </Show>
              <div>
                <p className="m-0 text-sm font-normal text-content-400">
                  {dictionary.Unit} ID
                </p>
                <p className="m-0 text-sm font-medium text-content-1000">
                  {unit?.external_id || "--"}
                </p>
              </div>
            </div>
          )}
        </>
      }
    >
      {type.isBoomScreen ? (
        <>
          <Tabs
            tabs={["Applications"]}
            selected={tab}
            setActiveTab={(t) => setActiveTab(t)}
            variant="underline"
          />

          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {tab === "Applications" && (
            <div className="mt-4">
              <ApplicationsTableView unitId={Number(unitId)} />
            </div>
          )}
        </>
      ) : null}

      {unit ? (
        <Suspense fallback={null}>
          <UnitSettings
            showSettings={showSettings}
            setShowSettings={setShowSettings}
            setShowEditModal={setShowEditModal}
            unit={unit}
            setShowDeleteUnitModal={setShowDeleteUnitModal}
            handleEditUnit={mutateAsync}
            templateName={unit.verification_template?.name}
            setShowReviewModal={setShowReviewModal}
          />
        </Suspense>
      ) : null}
      <TemplateReviewModal
        opened={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        id={unit?.verification_template?.id}
      />
      <DeleteUnitModal
        opened={showDeleteUnitModal}
        onClose={() => setShowDeleteUnitModal(false)}
        onDelete={deleteUnit}
      />

      <EditConfirmModal
        onChange={() => {
          if (!showEditModal.context) return;

          const overrideMap = {
            [EditModalContext.RentAmount]: {
              pms_monthly_rent_amount_override: false
            },
            [EditModalContext.HomeRentAmount]: {
              pms_home_rent_amount_override: false
            },
            [EditModalContext.SiteRentAmount]: {
              pms_site_rent_amount_override: false
            },
            [EditModalContext.CurrentStatus]: {
              pms_occupied_override: false,
              pms_available_at_override: false
            }
          };
          const override = overrideMap[showEditModal.context];

          mutateAsync({
            unitId: Number(unitId),
            settings: override
          });
        }}
        context={showEditModal.context}
        opened={showEditModal.isOpen}
        onClose={() => setShowEditModal({ isOpen: false })}
      />
      {showInviteApplicantsModal && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          unitId={unit?.id}
          // unitName={unit?.title}
          // propertyName={
          //   unit?.property.name ||
          //   getShortAddressFromAddress(unit?.property.address) ||
          //   ""
          // }
          propertyId={Number(unit?.property.id || 0)}
          onInviteRefreshKey={["APPLICANTS_TABLE"]}
        />
      )}
    </Layout>
  );
};
